import { useState } from 'react';
import { ArrowRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { DESTINATIONS } from '../data/destinations';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'eco-input';

const OFFERINGS = [
  { id: 'Experiences Offered', key: 'join.off.exp' },
  { id: 'Accommodation', key: 'join.off.stay' },
  { id: 'Transfers', key: 'join.off.tr' },
  { id: 'Full Packages', key: 'join.off.pkg' },
];
const BIZ_TYPES = [
  { id: 'Local Agency', key: 'join.biz.agency' },
  { id: 'DMC', key: 'join.biz.dmc' },
  { id: 'Tour Operator', key: 'join.biz.operator' },
  { id: 'Local Guide', key: 'join.biz.guide' },
  { id: 'Accommodation', key: 'join.biz.stay' },
  { id: 'Transfer Provider', key: 'join.biz.tr' },
  { id: 'Experience Provider', key: 'join.biz.exp' },
];

export default function JoinPage() {
  const { t } = useLang();
  useSEO('Join the Network | Eco Collection', 'Junte-se à Rede Eco Collection — rede de parceiros locais de turismo.');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    business_name: '', country: 'São Tomé e Príncipe', destination: '', contact_person: '', email: '',
    whatsapp: '', website: '', business_type: 'Local Agency', languages: '', services: '',
    offerings: [], regions: '', description: '',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (v) => setForm((f) => ({ ...f, offerings: f.offerings.includes(v) ? f.offerings.filter((x) => x !== v) : [...f.offerings, v] }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('failed');
      setSent(true);
    } catch {
      toast.error(t('join.error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="join-page">
      <PageHeader kicker="Partner With Us" title={t('join.t')} sub={t('join.s')} />
      <section className="bg-eco-cream py-14 md:py-20">
        <Reveal className="mx-auto max-w-3xl px-5 md:px-8">
          {sent ? (
            <div data-testid="join-success" className="rounded-2xl border border-eco-ink/10 bg-white p-10 text-center">
              <Check size={36} className="mx-auto text-eco-green" />
              <h2 className="mt-4 text-2xl leading-tight font-semibold tracking-[-0.02em] text-eco-ink">{t('join.sent')}</h2>
              <p className="mt-2 text-sm text-eco-ink/55">{t('join.pipeline')}</p>
            </div>
          ) : (
            <form data-testid="join-form" onSubmit={submit} className="grid gap-4 rounded-2xl border border-eco-ink/10 bg-white p-7 sm:grid-cols-2 md:p-9">
              <input data-testid="join-business" required value={form.business_name} onChange={(e) => set('business_name', e.target.value)} placeholder={`${t('join.f.business')} *`} className={inputCls} />
              <input data-testid="join-contact" value={form.contact_person} onChange={(e) => set('contact_person', e.target.value)} placeholder={t('join.f.contact')} className={inputCls} />
              <input data-testid="join-email" required type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder={`${t('form.email')} *`} className={inputCls} />
              <input data-testid="join-whatsapp" value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} placeholder={t('join.f.whatsapp')} className={inputCls} />
              <select data-testid="join-country" value={form.country} onChange={(e) => set('country', e.target.value)} className={inputCls}>
                {['São Tomé e Príncipe', 'Cabo Verde', 'Ghana', 'Kenya', 'Tanzania', 'Mauritius', 'Maldives'].map((c) => <option key={c}>{c}</option>)}
                <option value="Outro">{t('join.other')}</option>
              </select>
              <input data-testid="join-destination" value={form.destination} onChange={(e) => set('destination', e.target.value)} placeholder={t('join.f.destination')} className={inputCls} />
              <select data-testid="join-type" value={form.business_type} onChange={(e) => set('business_type', e.target.value)} className={inputCls}>
                {BIZ_TYPES.map((b) => <option key={b.id} value={b.id}>{t(b.key)}</option>)}
              </select>
              <input data-testid="join-website" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder={t('join.f.website')} className={inputCls} />
              <input data-testid="join-languages" value={form.languages} onChange={(e) => set('languages', e.target.value)} placeholder={`${t('join.f.languages')} (PT · EN · FR)`} className={inputCls} />
              <input data-testid="join-services" value={form.services} onChange={(e) => set('services', e.target.value)} placeholder={t('join.f.services')} className={inputCls} />
              <div className="sm:col-span-2">
                <p className="mb-2.5 text-xs font-semibold tracking-wide uppercase text-eco-ink/50">{t('join.offerings')}</p>
                <div className="flex flex-wrap gap-2">
                  {OFFERINGS.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      data-testid={`join-offering-${o.id.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => toggle(o.id)}
                      className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${form.offerings.includes(o.id) ? 'border-eco-green bg-eco-green text-white' : 'border-eco-ink/15 text-eco-ink/60 hover:border-eco-gold'}`}
                    >
                      {t(o.key)}
                    </button>
                  ))}
                </div>
              </div>
              <input data-testid="join-regions" value={form.regions} onChange={(e) => set('regions', e.target.value)} placeholder={t('join.f.regions')} className={`${inputCls} sm:col-span-2`} />
              <textarea data-testid="join-desc" rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder={t('join.f.desc')} className={`${inputCls} sm:col-span-2`} />
              <p className="text-xs text-eco-ink/45 sm:col-span-2">{t('join.soon')}</p>
              <button type="submit" data-testid="join-submit" disabled={sending} className="eco-btn-primary w-full disabled:opacity-60 sm:col-span-2">
                {sending ? <Loader2 size={16} className="animate-spin" /> : <>{t('join.submit')} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" /></>}
              </button>
            </form>
          )}
        </Reveal>
      </section>
    </div>
  );
}
